/**
 * Audio playback service using Web Audio API
 * Receives Opus-encoded audio frames from the server and plays them back.
 * Compatible with RemoteDesktop.Shared/Models/AudioModels.cs
 */

export interface AudioFrame {
    sessionId: string;
    sequenceNumber: number;
    timestamp: number;
    codec: number; // 0=PCM, 1=Opus, 2=AAC
    data: Uint8Array;
    sampleRate: number;
    channels: number;
    durationMs: number;
}

export interface AudioServiceEvents {
    onAudioLevelChanged?: (peak: number, rms: number) => void;
    onPlaybackStarted?: () => void;
    onPlaybackStopped?: () => void;
    onError?: (error: string) => void;
}

export class AudioService {
    private audioContext: AudioContext | null = null;
    private gainNode: GainNode | null = null;
    private nextPlayTime = 0;
    private isPlaying = false;
    private isMuted = false;
    private volume = 1.0;
    private sampleRate = 48000;
    private channels = 2;
    private events: AudioServiceEvents;
    private analyserNode: AnalyserNode | null = null;
    private levelCheckInterval: number | null = null;

    constructor(events: AudioServiceEvents = {}) {
        this.events = events;
    }

    /** Initialize the audio context (must be called after user interaction) */
    async initialize(): Promise<void> {
        if (this.audioContext) return;

        this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
        this.gainNode = this.audioContext.createGain();
        this.gainNode.gain.value = this.volume;

        // Analyser for audio level metering
        this.analyserNode = this.audioContext.createAnalyser();
        this.analyserNode.fftSize = 256;

        this.gainNode.connect(this.analyserNode);
        this.analyserNode.connect(this.audioContext.destination);

        this.nextPlayTime = this.audioContext.currentTime;

        // Start level monitoring
        this.startLevelMonitoring();
    }

    /** Process an incoming audio frame */
    async processAudioFrame(frame: AudioFrame): Promise<void> {
        if (!this.audioContext || !this.gainNode || this.isMuted) return;

        // Resume context if suspended (browser autoplay policy)
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        try {
            let audioBuffer: AudioBuffer;

            if (frame.codec === 0) {
                // PCM: decode directly
                audioBuffer = this.decodePCM(frame.data, frame.sampleRate, frame.channels);
            } else if (frame.codec === 1) {
                // Opus: use Web Audio API decodeAudioData
                audioBuffer = await this.decodeOpus(frame.data, frame.sampleRate, frame.channels);
            } else {
                console.warn(`Unsupported audio codec: ${frame.codec}`);
                return;
            }

            // Schedule playback
            const source = this.audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(this.gainNode);

            const now = this.audioContext.currentTime;
            if (this.nextPlayTime < now) {
                this.nextPlayTime = now;
            }
            source.start(this.nextPlayTime);
            this.nextPlayTime += audioBuffer.duration;

            if (!this.isPlaying) {
                this.isPlaying = true;
                this.events.onPlaybackStarted?.();
            }
        } catch (e) {
            console.error('Audio frame processing failed:', e);
            this.events.onError?.(`Audio decode error: ${e}`);
        }
    }

    /** Decode raw PCM data to AudioBuffer */
    private decodePCM(data: Uint8Array, sampleRate: number, channels: number): AudioBuffer {
        const ctx = this.audioContext!;

        // Copy to aligned buffer to avoid RangeError when byteOffset is odd
        const aligned = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
        const int16 = new Int16Array(aligned);
        const numSamples = int16.length / channels;
        const audioBuffer = ctx.createBuffer(channels, numSamples, sampleRate);

        for (let ch = 0; ch < channels; ch++) {
            const channelData = audioBuffer.getChannelData(ch);
            for (let i = 0; i < numSamples; i++) {
                channelData[i] = int16[i * channels + ch] / 32768.0;
            }
        }

        return audioBuffer;
    }

    /** Decode Opus data using Web Audio API */
    private async decodeOpus(data: Uint8Array, sampleRate: number, channels: number): Promise<AudioBuffer> {
        const ctx = this.audioContext!;

        // WC-12: Try decodeAudioData first (works if data is already in a container).
        // If raw Opus frame, wrap in minimal OGG/Opus container then retry.
        // Final fallback: treat as PCM.
        try {
            const arrayBuffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
            return await ctx.decodeAudioData(arrayBuffer);
        } catch {
            // Raw Opus frame — wrap in OGG container
            try {
                const ogg = this.wrapOpusInOgg(data, sampleRate, channels);
                return await ctx.decodeAudioData(ogg.buffer as ArrayBuffer);
            } catch {
                // Both decode attempts failed — return silence instead of
                // treating Opus data as PCM (which produces dangerous noise)
                console.warn('[Audio] Opus decode failed, returning silence');
                return ctx.createBuffer(channels, sampleRate / 50, sampleRate); // 20ms silence
            }
        }
    }

    /**
     * WC-12: Create minimal OGG/Opus container for a single Opus frame.
     * OGG structure: ID header page + Comment header page + Audio data page.
     */
    private wrapOpusInOgg(opusData: Uint8Array, sampleRate: number, channels: number): Uint8Array {
        // OGG page header helper
        const serialNo = 0x4C45564F; // "LEVO"
        let granulePos = 0;
        let pageSeq = 0;

        const createPage = (data: Uint8Array, headerType: number, isLast: boolean): Uint8Array => {
            const segCount = Math.ceil(data.length / 255);
            const headerSize = 27 + segCount;
            const page = new Uint8Array(headerSize + data.length);
            const view = new DataView(page.buffer);

            // "OggS" capture pattern
            page[0] = 0x4F; page[1] = 0x67; page[2] = 0x67; page[3] = 0x53;
            page[4] = 0; // version
            page[5] = headerType | (isLast ? 0x04 : 0);
            // Granule position (8 bytes, little-endian)
            view.setUint32(6, granulePos & 0xFFFFFFFF, true);
            view.setUint32(10, 0, true);
            view.setUint32(14, serialNo, true); // serial
            view.setUint32(18, pageSeq++, true); // page sequence
            view.setUint32(22, 0, true); // checksum (0 = skip)
            page[26] = segCount;
            // Segment table
            let remaining = data.length;
            for (let i = 0; i < segCount; i++) {
                page[27 + i] = Math.min(remaining, 255);
                remaining -= 255;
            }
            page.set(data, headerSize);
            return page;
        };

        // OpusHead header (19 bytes)
        const head = new Uint8Array(19);
        head.set([0x4F, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64]); // "OpusHead"
        head[8] = 1; // version
        head[9] = channels;
        // pre-skip (little-endian uint16)
        head[10] = 0x38; head[11] = 0x01; // 312 samples
        // input sample rate
        const srView = new DataView(head.buffer);
        srView.setUint32(12, sampleRate, true);
        // output gain = 0, channel mapping = 0
        head[16] = 0; head[17] = 0; head[18] = 0;
        const headPage = createPage(head, 0x02, false); // BOS flag

        // OpusTags (minimal)
        const vendor = new TextEncoder().encode('LEVOR');
        const tags = new Uint8Array(8 + 4 + vendor.length + 4);
        tags.set([0x4F, 0x70, 0x75, 0x73, 0x54, 0x61, 0x67, 0x73]); // "OpusTags"
        new DataView(tags.buffer).setUint32(8, vendor.length, true);
        tags.set(vendor, 12);
        new DataView(tags.buffer).setUint32(12 + vendor.length, 0, true); // 0 comments
        const tagsPage = createPage(tags, 0x00, false);

        // Audio data page
        granulePos = 960; // One frame at 48kHz = 20ms = 960 samples
        const audioPage = createPage(opusData, 0x00, true); // EOS flag

        // Combine all pages
        const result = new Uint8Array(headPage.length + tagsPage.length + audioPage.length);
        result.set(headPage, 0);
        result.set(tagsPage, headPage.length);
        result.set(audioPage, headPage.length + tagsPage.length);
        return result;
    }

    /** Set volume (0.0 to 1.0) */
    setVolume(value: number): void {
        this.volume = Math.max(0, Math.min(1, value));
        if (this.gainNode) {
            this.gainNode.gain.value = this.volume;
        }
    }

    /** Get current volume */
    getVolume(): number {
        return this.volume;
    }

    /** Toggle mute */
    setMuted(muted: boolean): void {
        this.isMuted = muted;
        if (this.gainNode) {
            this.gainNode.gain.value = muted ? 0 : this.volume;
        }
    }

    /** Get mute state */
    getMuted(): boolean {
        return this.isMuted;
    }

    /** Update audio settings */
    updateSettings(settings: { sampleRate?: number; channels?: number }): void {
        if (settings.sampleRate) this.sampleRate = settings.sampleRate;
        if (settings.channels) this.channels = settings.channels;
    }

    /** Start monitoring audio levels for UI */
    private startLevelMonitoring(): void {
        if (this.levelCheckInterval || !this.analyserNode) return;

        const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);

        this.levelCheckInterval = window.setInterval(() => {
            if (!this.analyserNode || !this.isPlaying) return;

            this.analyserNode.getByteTimeDomainData(dataArray);

            let peak = 0;
            let sum = 0;
            for (const sample of dataArray) {
                const normalized = (sample - 128) / 128;
                const abs = Math.abs(normalized);
                if (abs > peak) peak = abs;
                sum += normalized * normalized;
            }
            const rms = Math.sqrt(sum / dataArray.length);

            this.events.onAudioLevelChanged?.(peak, rms);
        }, 100); // 10 Hz update rate
    }

    /** WC-13: Get available audio output devices (Chrome/Edge only) */
    async getOutputDevices(): Promise<{ deviceId: string; label: string }[]> {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            return devices
                .filter(d => d.kind === 'audiooutput')
                .map(d => ({ deviceId: d.deviceId, label: d.label || `Speaker ${d.deviceId.slice(0, 8)}` }));
        } catch {
            return [];
        }
    }

    /** WC-13: Set output device (requires AudioContext with sinkId support — Chrome 110+) */
    async setOutputDevice(deviceId: string): Promise<boolean> {
        try {
            if (this.audioContext && 'setSinkId' in this.audioContext) {
                await (this.audioContext as any).setSinkId(deviceId);
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }

    /** Stop playback and clean up */
    stop(): void {
        this.isPlaying = false;
        if (this.levelCheckInterval) {
            clearInterval(this.levelCheckInterval);
            this.levelCheckInterval = null;
        }
        this.events.onPlaybackStopped?.();
    }

    /** Dispose all resources */
    dispose(): void {
        this.stop();
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        this.gainNode = null;
        this.analyserNode = null;
    }
}
